import { Navigate, Outlet, useLocation, useParams } from 'react-router';
import { useAuth } from '@/lib/auth/auth-context';
import { useGating } from '@/lib/auth/use-gating';
import { useMyPermissions } from '@/lib/auth/my-permissions';
import { defaultLocale, locales, type Locale } from '@/i18n/config';

export function ProtectedRoute() {
  const { user, isLoading } = useAuth();
  // ENG-YAKSON-080 (A7): route guard now gates on the backend's resolved
  // permissions (keysForRoute → hasPermission), falling back to the legacy
  // canAccessRoute(userType) matrix only if my-permissions fails to load.
  // Belt-and-suspenders for the RBAC settings screens (A8/A9 also gate in-page).
  const { canRoute } = useGating();
  // Wait for the resolved permissions before deciding. On a direct link (a
  // fresh page load) they are still in flight on the first render, and the
  // legacy fallback gives Finance to admins only — so a till user
  // (finance.create_sale, not admin) opening /finance/pos/create-invoice by
  // link was bounced to the Dashboard, while the same page opened from the
  // menu (permissions already loaded) worked. Waiting makes both paths take
  // the same decision for every role. If the fetch FAILS it is still settled
  // and the legacy matrix decides, exactly as before. Only the FIRST fetch is
  // waited for: a later refetch keeps the page on screen.
  const { settled: permissionsSettled } = useMyPermissions();
  const location = useLocation();
  const { locale } = useParams<{ locale: string }>();
  const validLocale = locales.includes(locale as Locale) ? locale : defaultLocale;

  if (isLoading) {
    return null;
  }

  if (!user) {
    const callbackUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/${validLocale}/login?callbackUrl=${callbackUrl}`} replace />;
  }

  const pathnameWithoutLocale = location.pathname.replace(
    new RegExp(`^/(${locales.join('|')})`),
    '',
  ) || '/';

  if (!permissionsSettled) {
    return null;
  }

  if (!canRoute(pathnameWithoutLocale)) {
    return <Navigate to={`/${validLocale}/dashboard`} replace />;
  }

  return <Outlet />;
}
